import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { stepKeyDirection, threadStepperPosition, type StepDirection } from '../../../lib/outreach-thread-stepper';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { IconButton, Typography } from '../ui';
import styles from './thread-stepper.module.css';

/**
 * One row of the list a thread dialog was opened from. `render` builds the dialog THIS row opens — a
 * received thread's `OutreachThreadModal`, a sent thread's `OutreachCardModal` — handed the same list, so
 * the stepper carries on across kinds.
 */
export interface ThreadSibling {
  /** The thread's own outreach id (received and sent threads are separate records, so ids never collide). */
  id: string;
  render: (siblings: readonly ThreadSibling[]) => ReactNode;
}

/**
 * Previous / Next between the opener's threads (`lib/outreach-thread-stepper.ts`), shared by both thread
 * dialogs so the counter, buttons, keys and BI cannot drift between them:
 *
 * - "{index} of {total}" then the Previous / Next chevrons (`aria-label` + tooltip
 *   `outreach.thread.previous` / `.next`), disabled at the ends — nothing wraps;
 * - ←/→ step while focus is not in a text field, mirrored in RTL (→ Previous), as the chevrons are;
 * - a step REPLACES the stack's top entry (`replaceModal`) with the target row's own dialog: no history,
 *   and the same dialog type stays mounted;
 * - BI `outreach_thread_previous` / `outreach_thread_next` with `via: button | keyboard`.
 *
 * Renders nothing when there is no list, one thread, or the current one is not in it.
 */
export function ThreadStepper({
  siblings,
  currentId,
  page,
  className,
}: {
  siblings?: readonly ThreadSibling[];
  currentId: string;
  /** The dialog's src:172 page fields, spread into the click. */
  page: { page_name: string; page_version?: string };
  className?: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { replaceModal } = useTaskSidePanel();
  const position = threadStepperPosition(siblings, currentId);

  const step = (direction: StepDirection, via: 'button' | 'keyboard') => {
    const target = direction === 'previous' ? position?.previous : position?.next;
    if (!position || !target || !siblings) return;
    logPageClickBi(
      { ...page, button_name: `outreach_thread_${direction}`, task_id: target.id },
      { additionalFields: { outreach_id: currentId, via, index: String(position.index), total: String(position.total) } },
    );
    replaceModal(target.render(siblings), target.id);
  };
  const live = useRef(step);
  live.current = step;

  // The dialog holding this is the top of the stack whenever it is mounted, so nothing else listens.
  const shown = position !== null;
  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const rtl = document.documentElement.dir === 'rtl';
      const direction = stepKeyDirection(event, event.target instanceof HTMLElement ? event.target : null, rtl);
      if (!direction) return;
      event.preventDefault();
      live.current(direction, 'keyboard');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shown]);

  if (!position) return null;
  return (
    <div className={[styles.stepper, className].filter(Boolean).join(' ')}>
      <Typography variant="p3" color="secondary" className={styles.position} aria-live="polite">
        {t('outreach.thread.position', { index: position.index, total: position.total })}
      </Typography>
      <IconButton
        size="small"
        variant="text"
        color="standard"
        aria-label={t('outreach.thread.previous')}
        title={t('outreach.thread.previous')}
        disabled={!position.previous}
        onClick={() => step('previous', 'button')}
      >
        <ChevronLeft size={16} aria-hidden className="dir-flip" />
      </IconButton>
      <IconButton
        size="small"
        variant="text"
        color="standard"
        aria-label={t('outreach.thread.next')}
        title={t('outreach.thread.next')}
        disabled={!position.next}
        onClick={() => step('next', 'button')}
      >
        <ChevronRight size={16} aria-hidden className="dir-flip" />
      </IconButton>
    </div>
  );
}

/** A step lands at the top of the next thread: scroll the nearest scrolling ancestor of `ref` to 0 when
 *  `id` changes (not on first mount). */
export function useScrollTopOnStep(ref: RefObject<HTMLElement | null>, id: string): void {
  const shownId = useRef(id);
  useEffect(() => {
    if (shownId.current === id) return;
    shownId.current = id;
    let node: HTMLElement | null = ref.current?.parentElement ?? null;
    while (node) {
      const overflow = getComputedStyle(node).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) {
        node.scrollTop = 0;
        break;
      }
      node = node.parentElement;
    }
  }, [ref, id]);
}
